import { Box, Flex, Heading } from "@radix-ui/themes";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import {
	invisalignArticleFaqs,
	invisalignArticleTitle,
} from "~/data/invisalignArticle";
import styles from "../live-seo-page.module.css";
import articleStyles from "./invisalign-article.module.css";

const comparison = [
	[
		"Appearance",
		"Clear, nearly invisible aligners",
		"Visible metal or ceramic brackets",
	],
	[
		"Comfort",
		"Smooth plastic, minimal irritation",
		"Potential wire and bracket discomfort",
	],
	[
		"Removability",
		"Remove for meals and brushing",
		"Fixed throughout treatment",
	],
	[
		"Best for",
		"Mild to moderate cases",
		"Complex cases requiring precise control",
	],
	[
		"Oral care",
		"Normal brushing and flossing",
		"Special flossing tools needed",
	],
	["Visit frequency", "Every 6-8 weeks", "Every 4-6 weeks"],
];

// Preserve the canonical article's English copy; surrounding navigation/form
// continue to follow the visitor's language preference.
export function InvisalignArticlePage() {
	return (
		<Flex direction="column">
			<article lang="en" className={styles.articleBand}>
				<Box className={styles.articleInner}>
					<OptimizedImage
						src="/adobestock-invisalign.jpg"
						alt="Smiling woman holding a clear Invisalign aligner"
						width={1024}
						height={682}
						className={styles.featuredImage}
						fetchPriority="high"
						sizes="(max-width: 767px) 92vw, 1000px"
					/>
					<Heading as="h1" className={styles.postTitle}>
						{invisalignArticleTitle}
					</Heading>
					<p className={styles.postDate}>
						<time dateTime="2026-05-19">May 19, 2026</time>
					</p>
					<div className={`${styles.entryContent} ${articleStyles.content}`}>
						<p>
							<Link to="/invisalign/">Invisalign</Link> uses custom-made clear
							aligners to gradually shift teeth into proper alignment without
							metal brackets or wires. Made from SmartTrack thermoplastic
							material that’s FDA-approved, these removable trays fit snugly
							over your teeth and are worn 20-22 hours daily. You’ll switch to a
							new set of aligners every one to two weeks as your teeth move into
							position. Invisalign treats everything from mild crowding to more
							complex orthodontic cases, making it a popular choice for{" "}
							<Link to="/orthodontics-for-teens/">teens and adults</Link> who
							want a discreet path to a beautiful smile.
						</p>
						<img
							src="/invisalign-article-teeth.jpg"
							alt="Model of teeth demonstrating proper alignment"
							width={1024}
							height={682}
							className={styles.inlinePostImage}
							loading="lazy"
						/>
						<h2>How Invisalign Works: The Treatment Process</h2>
						<p>
							Getting started with <Link to="/invisalign/">Invisalign</Link> is
							straightforward. Here’s what to expect:
						</p>
						<ol>
							<li>
								<strong>
									<Link to="/free-consult/">Free consultation</Link> and digital
									scan
								</strong>{" "}
								– Dr. Garcia-Paul uses the iTero digital scanner to capture
								precise 3D images of your teeth. No messy impressions needed.
							</li>
							<li>
								<strong>Custom treatment plan</strong> – Using ClinCheck
								software, you’ll see a digital preview of how your teeth will
								move at each stage. You can actually watch your future smile
								take shape before treatment begins.
							</li>
							<li>
								<strong>Aligners made just for you</strong> – Based on your
								treatment plan, a complete series of aligners is fabricated
								specifically for your mouth.
							</li>
							<li>
								<strong>Switch trays every 1-2 weeks</strong> – Each new set of
								aligners applies gentle pressure to continue moving your teeth
								according to plan.
							</li>
							<li>
								<strong>Periodic check-ins</strong> – You’ll visit{" "}
								<Link to="/">Miami Orthodontic Specialists</Link> every six to
								eight weeks so Dr. Garcia-Paul can monitor your progress and
								make any adjustments.
							</li>
							<li>
								<strong>Retainers lock in your results</strong> – After
								treatment, custom retainers keep your teeth in their new
								positions for the long term.
							</li>
						</ol>
						<p>
							The entire process typically takes between 6 and 18 months,
							depending on your specific needs.
						</p>
						<h2>Top Reasons Patients Are Choosing Invisalign</h2>
						<p>
							Why are so many people opting for clear aligners? The benefits
							speak for themselves:
						</p>
						<h3>Nearly invisible appearance</h3>
						<p>
							Most people won’t notice you’re wearing them. This matters for
							professionals, students, and anyone who wants to straighten their
							teeth without drawing attention.
						</p>
						<h3>Removable for eating and special occasions</h3>
						<p>
							Take them out for meals, photos, or important events. No food
							restrictions like you’d have with{" "}
							<Link to="/metal-braces/">traditional braces</Link>.
						</p>
						<h3>More comfortable fit</h3>
						<p>
							Smooth plastic aligners mean no metal brackets poking your cheeks
							or wires irritating your gums.
						</p>
						<h3>Fewer office visits</h3>
						<p>
							Check-ins happen every six to eight weeks rather than every four
							weeks. That’s less time away from work or school.
						</p>
						<h3>Easier oral hygiene</h3>
						<p>
							Brush and floss normally. No special tools or techniques required.
							This reduces your risk of cavities and gum problems during
							treatment.
						</p>
						<h3>See your results before you start</h3>
						<p>
							The digital treatment preview shows exactly how your smile will
							look when you’re done. No guesswork involved.
						</p>
						<h2>Invisalign vs. Traditional Braces: Key Differences</h2>
						<section
							className={articleStyles.tableScroll}
							aria-label="Treatment comparison — scroll horizontally on small screens"
							// biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard users need to scroll this table on narrow screens.
							tabIndex={0}
						>
							<table
								className={articleStyles.table}
								aria-label="Invisalign and traditional braces comparison"
							>
								<thead>
									<tr>
										<th scope="col">Feature</th>
										<th scope="col">
											<Link to="/invisalign/">Invisalign</Link>
										</th>
										<th scope="col">
											<Link to="/metal-braces/">Traditional Braces</Link>
										</th>
									</tr>
								</thead>
								<tbody>
									{comparison.map(([feature, aligners, braces]) => (
										<tr key={feature}>
											<th scope="row">{feature}</th>
											<td>{aligners}</td>
											<td>{braces}</td>
										</tr>
									))}
								</tbody>
							</table>
						</section>
						<p>
							Both options deliver excellent results. Traditional braces handle
							the most complex cases with predictable precision. Invisalign
							works well for many orthodontic issues while offering lifestyle
							flexibility. During your{" "}
							<Link to="/free-consult/">free consultation</Link>, Dr.
							Garcia-Paul will recommend the treatment that’s best for your
							specific situation.
						</p>
						<h2>What Affects the Cost of Invisalign?</h2>
						<p>
							Invisalign costs depend on several factors unique to your
							treatment. Case complexity plays the biggest role. Minor crowding
							requires fewer aligners than full-arch correction, and longer
							treatments with more trays naturally cost more than shorter ones.
						</p>
						<p>
							Your orthodontist’s experience matters too. Board-certified
							orthodontists like{" "}
							<Link to="/meet-our-doctors/">Dr. Garcia-Paul</Link> have
							completed specialty training beyond dental school, which means
							you’re getting expert care.
						</p>
						<p>
							Insurance often covers Invisalign the same way it covers braces.
							Many plans include orthodontic benefits that can significantly
							reduce your out-of-pocket costs.{" "}
							<Link to="/">Miami Orthodontic Specialists</Link> offers{" "}
							<Link to="/insurance-finances/">flexible payment options</Link>{" "}
							and works with any budget to make treatment accessible.
						</p>
						<p>
							Your total investment typically includes diagnostic records, all
							aligners throughout treatment, and retainers to maintain your
							results afterward.
						</p>
						<h2>Who Is a Good Candidate for Invisalign?</h2>
						<p>
							Good candidates for Invisalign include{" "}
							<Link to="/orthodontics-for-adults/">
								teens and adults with mild to moderate alignment issues
							</Link>
							. The system effectively treats:
						</p>
						<ul>
							{[
								"Crowded teeth",
								"Gaps between teeth",
								"Overbite",
								"Underbite",
								"Crossbite",
								"Open bite",
							].map((item) => (
								<li key={item}>{item}</li>
							))}
						</ul>
						<p>
							Success requires commitment. You’ll need to wear your aligners
							20-22 hours daily, removing them only for eating and brushing.
							Skipping wear time slows your progress.
						</p>
						<p>
							Healthy teeth and gums are essential before starting. Any cavities
							or gum disease should be treated first.
						</p>
						<p>
							Some complex skeletal issues may require{" "}
							<Link to="/metal-braces/">traditional braces</Link> or other
							treatments. That’s why a consultation matters. Dr. Garcia-Paul
							evaluates your teeth, bite, and jaw to determine if Invisalign
							will give you the results you want.
						</p>
						<h2>Frequently Asked Questions About Invisalign</h2>
						{invisalignArticleFaqs.map(({ question, answer }) => (
							<section key={question}>
								<h3>{question}</h3>
								<p>{answer}</p>
							</section>
						))}
						<p>
							<strong>Ready to start your smile journey?</strong> Dr. Carmen
							Garcia-Paul, a board-certified orthodontist with specialty
							training in Orthodontics and Dentofacial Orthopedics, provides
							individualized attention to every member of the MOS family.
							Schedule your <Link to="/free-consult/">free consultation</Link>{" "}
							to find out if <Link to="/invisalign/">Invisalign</Link> can give
							you the beautiful smile you’ve always dreamed of.
						</p>
					</div>
				</Box>
			</article>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
